import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { approvalsAPI } from '../../services/api';

const AprobacionesConfig = () => {
  const { showSuccess, showError, showConfirm } = useToast();
  const [solicitudes, setSolicitudes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [comentarios, setComentarios] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pendingRes, historyRes, statsRes] = await Promise.all([
        approvalsAPI.getPendingApprovals(),
        approvalsAPI.getApprovalHistory(),
        approvalsAPI.getApprovalStats()
      ]);
      
      setSolicitudes(pendingRes.data);
      setHistorial(historyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (solicitud, action) => {
    setSelectedSolicitud(solicitud);
    setModalAction(action);
    setComentarios('');
    setShowModal(true);
  };

  const confirmAction = async () => {
    try {
      if (modalAction === 'approve') {
        await approvalsAPI.approveRequest(selectedSolicitud.id, { comentarios });
      } else {
        await approvalsAPI.rejectRequest(selectedSolicitud.id, { comentarios });
      }
      
      setShowModal(false);
      setSelectedSolicitud(null);
      setComentarios('');
      
      // Recargar datos
      await loadData();
      
      showError(modalAction === 'approve' 
        ? 'Solicitud aprobada exitosamente' 
        : 'Solicitud rechazada exitosamente'
      );
    } catch (error) {
      console.error('Error procesando solicitud:', error);
      showError('Error procesando la solicitud');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      PENDIENTE: { color: '#ffc107', text: 'Pendiente', icon: '⏳' },
      APROBADO: { color: '#28a745', text: 'Aprobado', icon: '✅' },
      RECHAZADO: { color: '#dc3545', text: 'Rechazado', icon: '❌' }
    };
    const badge = badges[estado] || { color: '#6c757d', text: estado, icon: '❓' };
    
    return (
      <span style={{
        backgroundColor: badge.color,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.875rem',
        fontWeight: 'bold'
      }}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="sr-only">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>🔐 Gestión de Aprobaciones</h3>
        {stats && (
          <div className="d-flex gap-3">
            <div className="text-center">
              <div className="h4 mb-0" style={{ color: '#ffc107' }}>{stats.pendientes}</div>
              <small className="text-muted">Pendientes</small>
            </div>
            <div className="text-center">
              <div className="h4 mb-0" style={{ color: '#28a745' }}>{stats.aprobadas}</div>
              <small className="text-muted">Aprobadas</small>
            </div>
            <div className="text-center">
              <div className="h4 mb-0" style={{ color: '#dc3545' }}>{stats.rechazadas}</div>
              <small className="text-muted">Rechazadas</small>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            ⏳ Pendientes ({solicitudes.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📋 Historial ({historial.length})
          </button>
        </li>
      </ul>

      {/* Contenido de tabs */}
      {activeTab === 'pending' && (
        <div className="card">
          <div className="card-header">
            <h5>Solicitudes Pendientes de Aprobación</h5>
          </div>
          <div className="card-body">
            {solicitudes.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                <p className="mt-3 text-muted">No hay solicitudes pendientes</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th>Fecha Solicitud</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solicitudes.map(solicitud => (
                      <tr key={solicitud.id}>
                        <td>
                          <strong>{solicitud.usuario.nombre}</strong>
                        </td>
                        <td>{solicitud.usuario.email}</td>
                        <td>
                          <span className="badge bg-secondary">{solicitud.usuario.rol}</span>
                        </td>
                        <td>{formatDate(solicitud.fechaSolicitud)}</td>
                        <td>
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleAction(solicitud, 'approve')}
                              title="Aprobar solicitud"
                            >
                              <i className="fas fa-check"></i> Aprobar
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleAction(solicitud, 'reject')}
                              title="Rechazar solicitud"
                            >
                              <i className="fas fa-times"></i> Rechazar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card">
          <div className="card-header">
            <h5>Historial de Solicitudes</h5>
          </div>
          <div className="card-body">
            {historial.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-history text-muted" style={{ fontSize: '3rem' }}></i>
                <p className="mt-3 text-muted">No hay historial de solicitudes</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th>Revisor</th>
                      <th>Fecha Revisión</th>
                      <th>Comentarios</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map(solicitud => (
                      <tr key={solicitud.id}>
                        <td>
                          <strong>{solicitud.usuario.nombre}</strong>
                        </td>
                        <td>{solicitud.usuario.email}</td>
                        <td>
                          <span className="badge bg-secondary">{solicitud.usuario.rol}</span>
                        </td>
                        <td>{getEstadoBadge(solicitud.estado)}</td>
                        <td>{solicitud.revisor?.nombre || '-'}</td>
                        <td>{formatDate(solicitud.fechaRevision)}</td>
                        <td>
                          {solicitud.comentarios ? (
                            <span className="text-muted" title={solicitud.comentarios}>
                              {solicitud.comentarios.length > 50 
                                ? solicitud.comentarios.substring(0, 50) + '...' 
                                : solicitud.comentarios
                              }
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      {showModal && (
        <div 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }} 
          onClick={() => setShowModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '0',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                {modalAction === 'approve' ? '✅ Aprobar Solicitud' : '❌ Rechazar Solicitud'}
              </h5>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6c757d'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px' }}>
                ¿Estás seguro de que quieres{' '}
                <strong>{modalAction === 'approve' ? 'aprobar' : 'rechazar'}</strong> la solicitud de{' '}
                <strong>{selectedSolicitud?.usuario?.nombre}</strong>?
              </p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Comentarios (opcional):
                </label>
                <textarea
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  value={comentarios}
                  onChange={(e) => setComentarios(e.target.value)}
                  placeholder={`Comentarios sobre la ${modalAction === 'approve' ? 'aprobación' : 'rechazo'}...`}
                ></textarea>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmAction}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: modalAction === 'approve' ? '#28a745' : '#dc3545',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {modalAction === 'approve' ? 'Aprobar' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* El overlay ya está integrado en el modal principal */}
    </div>
  );
};

export default AprobacionesConfig;
